import { allowedRef } from "@/helpers/login";
import { portalClienteNewPassword } from "@/helpers/portal-cliente";
import { Customer } from "@/types/customer"
import { Session } from "@/types/session";
import { MutateOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import Cookies from 'js-cookie';
import { DOMAIN_NAME, SESSION_NAME } from "@/helpers/constants";

export type SessionProviderProps = PropsWithChildren<{}>;

export type LoginData = {
  login: string;
  password: string;
}

export type SessionProviderContext = {
  customer?: Customer;
  expiresIn: number;
  token: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  isFetched?: boolean;
  mutateStatus: "error" | "success" | "idle" | "pending";

  login: (variables: LoginData, options?: MutateOptions<any, Error, LoginData, unknown> | undefined) => void;
  logout: () => void;
}

const Context = createContext<SessionProviderContext>({
  expiresIn: 0,
  token: "",
} as SessionProviderContext);

export function SessionProvider({ children }: SessionProviderProps) {
  const router = useRouter();

  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof router.query.token === 'string') {
      setToken(router.query.token);
    }
  }, [router.query.token]);

  const { data, isError, isFetching, isLoading, isFetched, refetch } = useQuery<Session>({
    queryKey: ['session', token],
    queryFn: async () => {
      const url = token ? `/api/auth/session?token=${token}` : '/api/auth/session';
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Unauthorized");
      }

      return response.json();
    },
    retry(failureCount, error) {
      if (error instanceof Error) {
        if (error.message === "Unauthorized") {
          return false;
        }
      }

      return failureCount < 2;
    },
  });

  const loginMutation = useMutation({
    mutationKey: ['login'],
    mutationFn: async (values: LoginData) => {
      const response = await fetch("/api/auth/login", {
        method: 'POST',
        body: JSON.stringify(values)
      });

      if (!response.ok) {
        throw new Error("Unauthorized");
      }

      return response.json();
    },
    onSuccess: async (data, variables) => {
      setToken(data.token);

      const redirectQuery = {
        ...router.query,
      }

      delete redirectQuery.ref;
      delete redirectQuery.token;

      // Redireciona para ativaÃ§Ã£o de conta
      if (data && data.success && data.status === 'NEW_PASSWORD_REQUIRED') {
        router.push(portalClienteNewPassword(variables.login, data.session, redirectQuery));
      } else if (data && data.success && data.status === 'LOGIN_SUCCESS') {
        // login com sucesso
        refetch();

        if (
          !router.query.pickup_store
          || !router.query.return_store
          || !router.query.pickup_date
          || !router.query.return_date
          || !router.query.pickup_time
          || !router.query.return_time
        ) {
          router.push({
            pathname: '/',
            query: redirectQuery,
          });
        } else {
          router.push({
            pathname:
              typeof router.query.ref === 'string' && allowedRef.includes(router.query.ref as (typeof allowedRef)[number])
                ? router.query.ref : '/criar-reserva',
            query: redirectQuery,
          });
        }
      }
    },
  });

  const logout = async () => {
    setToken("invalid_token");
    await fetch('/api/auth/logout', { method: 'POST' });
    refetch();
  }

  return (
    <Context.Provider value={{
      login: loginMutation.mutate,
      logout,

      customer: data?.customer,
      expiresIn: data?.expiresIn || 0,
      token: data?.token || "",

      isError,
      isLoading: isLoading,
      isFetching: isFetching || loginMutation.status === 'pending',
      isFetched: isFetched,
      mutateStatus: loginMutation.status,
    }}>
      {children}
    </Context.Provider>
  )
}

export function useSession() {
  return useContext(Context);
}
